import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  favoriteProject,
  getFavoriteStats,
  getFavoritedProjectIdsAmong,
  listSavedProjects,
  unfavoriteProject,
} from '@/api/favorites'
import type { FavoriteToggleResponse, SavedProjectsPage } from '@/api/types'

export const FAVORITES_QUERY_KEY = ['favorites'] as const

/** Phase 8 alignment — saved-tab cards carry signed hero URLs with 1-hour TTL. */
const SIGNED_URL_STALE_MS = 50 * 60 * 1000

/**
 * Single-project favorite-stats. Used by Project Detail to populate the header heart + count
 * in parallel with the project hydrate query — see PHASE10_STATUS.md for why these fields
 * aren't on `ProjectDetail` itself.
 */
export const useFavoriteStats = (projectId: string | undefined) =>
  useQuery<FavoriteToggleResponse>({
    queryKey: [...FAVORITES_QUERY_KEY, 'stats', projectId],
    queryFn: () => getFavoriteStats(projectId as string),
    enabled: Boolean(projectId),
  })

interface FavoriteToggleVars {
  projectId: string
  /** The current isFavoritedByMe value before the click. The mutation flips it. */
  currentlyFavorited: boolean
}

interface FavoriteToggleCtx {
  previous: FavoriteToggleResponse | undefined
}

/**
 * Optimistic toggle. Surface-agnostic — takes only the projectId — so Phase 14's feed surface
 * can drop this on feed items unchanged. Callers pass the current `isFavoritedByMe` so we
 * know which direction to flip.
 *
 * Server is idempotent; retries / double-clicks are safe.
 */
export const useFavoriteToggle = () => {
  const queryClient = useQueryClient()
  return useMutation<FavoriteToggleResponse, unknown, FavoriteToggleVars, FavoriteToggleCtx>({
    mutationFn: ({ projectId, currentlyFavorited }) =>
      currentlyFavorited ? unfavoriteProject(projectId) : favoriteProject(projectId),
    onMutate: async ({ projectId, currentlyFavorited }) => {
      const key = [...FAVORITES_QUERY_KEY, 'stats', projectId]
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<FavoriteToggleResponse>(key)
      if (previous) {
        queryClient.setQueryData<FavoriteToggleResponse>(key, {
          ...previous,
          favoriteCount: previous.favoriteCount + (currentlyFavorited ? -1 : 1),
          isFavoritedByMe: !currentlyFavorited,
        })
      }
      return { previous }
    },
    onError: (_err, { projectId }, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData([...FAVORITES_QUERY_KEY, 'stats', projectId], ctx.previous)
      }
    },
    onSuccess: (data, { projectId }) => {
      queryClient.setQueryData([...FAVORITES_QUERY_KEY, 'stats', projectId], data)
    },
    onSettled: () => {
      // Invalidate downstream surfaces:
      //  - list of saved projects on the dashboard
      //  - bulk favorited-ids cache that drives browse-card hearts
      void queryClient.invalidateQueries({ queryKey: [...FAVORITES_QUERY_KEY, 'saved'] })
      void queryClient.invalidateQueries({ queryKey: [...FAVORITES_QUERY_KEY, 'ids-among'] })
    },
  })
}

interface UseMyFavoritesOptions {
  page?: number
  size?: number
  enabled?: boolean
}

export const useMyFavorites = (options: UseMyFavoritesOptions = {}) => {
  const { page = 0, size = 20, enabled = true } = options
  return useQuery<SavedProjectsPage>({
    queryKey: [...FAVORITES_QUERY_KEY, 'saved', { page, size }],
    queryFn: () => listSavedProjects({ page, size }),
    placeholderData: keepPreviousData,
    staleTime: SIGNED_URL_STALE_MS,
    enabled,
  })
}

/**
 * Bulk enrichment for browse-card hearts. Fetches the intersection of `projectIds` with the
 * current viewer's favorites in one query — no N+1 over the page. Disabled for unauthenticated
 * viewers (the hook returns an empty Set in that case so consumers don't need to branch).
 */
export const useMyFavoritedIds = (projectIds: string[], enabled: boolean = true) => {
  // Stable query key independent of input order.
  const stableIds = [...new Set(projectIds)].sort()
  return useQuery<Set<string>>({
    queryKey: [...FAVORITES_QUERY_KEY, 'ids-among', stableIds],
    queryFn: async () => new Set(await getFavoritedProjectIdsAmong(stableIds)),
    enabled: enabled && stableIds.length > 0,
    staleTime: 60 * 1000,
  })
}
